import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { FaShoppingBasket } from "react-icons/fa";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AdminColorBasketMiniMap, {
    colorBasketNumberBadgeSx,
} from "../../../admin/jigsaw/play/AdminColorBasketMiniMap";
import LoginConfirmDialog from "../../components/LoginConfirmDialog";
import { usePagesTranslation } from "../../locales";
import UserService from "../../../services/UserService";
import SubscriptionService from "../../../services/SubscriptionService";
import { SUBSCRIPTION_PLANS } from "../../subscription/PanelPlan";
import JigsawColorBasketPlayService, {
    preferColorBasketCount,
} from "./JigsawColorBasketPlayService";
import {
    formatColorBasketDebugLines,
    reportColorBasketsUnavailable,
} from "./colorBasketsUnavailable";

function basketNeedsUnlock(basket) {
    if (!basket) return true;
    if (basket.requiresUnlock === true) return true;
    if (basket.unlocked === false) return true;
    return false;
}

/**
 * Color-basket select dialog — unlocks with coins if needed, then opens basket play.
 * Pass `onBasketChosen` to handle selection in-place (e.g. recording capture); otherwise navigates to the public basket route.
 */
export default function JigsawColorBasketOverlay({
    open,
    onClose,
    storyId,
    jigsawId,
    grid,
    imageUrl,
    puzzleTitle,
    /** When true, show the Diagnostics panel (ADMIN only). */
    isAdminRole = false,
    /**
     * Optional in-place handler: (basketIndex, { basketCount, baskets, basket }) => void
     * When set, skips navigation to `/puzzle/play-jigsaw/.../basket/...`.
     */
    onBasketChosen = null,
    /** Optional overrides so fixed-size capture frames can center the dialog in the frame. */
    dialogSx = null,
    paperSx = null,
    /**
     * Promo capture only: once the baskets are on screen, open this basket by itself so a
     * scripted demo can film the chooser. Skips unlock/coins (nothing is spent).
     */
    autoPickBasketIndex = null,
    autoPickDelayMs = 1600,
}) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [meta, setMeta] = useState(null);
    const [result, setResult] = useState(null);
    const [basketCount, setBasketCount] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [loadingBaskets, setLoadingBaskets] = useState(false);
    const [error, setError] = useState("");
    /** basketIndex → play session ({ playState, isSolved, … }) */
    const [sessionByIndex, setSessionByIndex] = useState({});
    const [unlockingIndex, setUnlockingIndex] = useState(null);
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [insufficientCoinsOpen, setInsufficientCoinsOpen] = useState(false);
    const basketScrollRef = useRef(null);
    const [basketScrollHints, setBasketScrollHints] = useState({ left: false, right: false });

    const baskets = useMemo(
        () => (Array.isArray(result?.baskets) ? result.baskets : []),
        [result],
    );

    const unlockCoinCost = useMemo(() => {
        const fromResult = Number(result?.unlockCoinCost);
        if (Number.isFinite(fromResult) && fromResult > 0) return fromResult;
        const fromMeta = Number(meta?.unlockCoinCost);
        if (Number.isFinite(fromMeta) && fromMeta > 0) return fromMeta;
        return 5;
    }, [result?.unlockCoinCost, meta?.unlockCoinCost]);

    const freeMaxCount = useMemo(() => {
        const fromResult = Number(result?.freeMaxCount);
        if (Number.isFinite(fromResult) && fromResult >= 0) return fromResult;
        const fromMeta = Number(meta?.freeMaxCount);
        if (Number.isFinite(fromMeta) && fromMeta >= 0) return fromMeta;
        return 1;
    }, [result?.freeMaxCount, meta?.freeMaxCount]);

    const allowedBasketCounts = useMemo(() => {
        const raw = Array.isArray(result?.allowedCounts)
            ? result.allowedCounts
            : Array.isArray(meta?.allowedCounts)
              ? meta.allowedCounts
              : [];
        return raw.map(Number).filter((n) => Number.isFinite(n) && n >= 2);
    }, [result?.allowedCounts, meta?.allowedCounts]);

    const debugLines = useMemo(
        () =>
            formatColorBasketDebugLines({
                meta,
                result,
                basketCount,
                grid,
                imageUrl,
                error,
                sessionByIndex,
            }),
        [meta, result, basketCount, grid, imageUrl, error, sessionByIndex],
    );

    const showDiagnostics = Boolean(isAdminRole) && (Boolean(error) || baskets.length > 0);

    const updateBasketScrollHints = useCallback(() => {
        const el = basketScrollRef.current;
        if (!el) {
            setBasketScrollHints({ left: false, right: false });
            return;
        }
        const maxScroll = el.scrollWidth - el.clientWidth;
        const left = el.scrollLeft > 4;
        const right = maxScroll > 4 && el.scrollLeft < maxScroll - 4;
        setBasketScrollHints((prev) =>
            prev.left === left && prev.right === right ? prev : { left, right },
        );
    }, []);

    useEffect(() => {
        updateBasketScrollHints();
        const el = basketScrollRef.current;
        if (!el) return undefined;
        const onScroll = () => updateBasketScrollHints();
        el.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", updateBasketScrollHints);
        const ro =
            typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateBasketScrollHints) : null;
        ro?.observe(el);
        return () => {
            el.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", updateBasketScrollHints);
            ro?.disconnect();
        };
    }, [updateBasketScrollHints, baskets.length, open, loadingBaskets]);

    const scrollBasketsBy = useCallback((dir) => {
        const el = basketScrollRef.current;
        if (!el) return;
        const step = Math.max(120, Math.floor(el.clientWidth * 0.7));
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    }, []);

    useEffect(() => {
        if (!open) {
            setError("");
            setUnlockingIndex(null);
            setLoginDialogOpen(false);
            setInsufficientCoinsOpen(false);
            return undefined;
        }
        if (!jigsawId) return undefined;

        let cancelled = false;
        (async () => {
            setLoadingMeta(true);
            setError("");
            try {
                const m = await JigsawColorBasketPlayService.getColorBaskets(jigsawId);
                if (cancelled) return;
                setMeta(m);
                const allowedRaw = Array.isArray(m?.allowedCounts) ? m.allowedCounts : [];
                const allowed = allowedRaw
                    .map(Number)
                    .filter((n) => Number.isFinite(n) && n >= 2);
                if (m?.available === false) {
                    const reason = reportColorBasketsUnavailable(m, "JigsawColorBasketOverlay/meta");
                    setError(
                        t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                    );
                    setBasketCount(null);
                } else if (allowed.length === 0) {
                    const reason = reportColorBasketsUnavailable(
                        {
                            ...m,
                            reason:
                                m?.reason ||
                                "no_allowed_counts (server returned available but empty allowedCounts)",
                        },
                        "JigsawColorBasketOverlay/meta",
                    );
                    setError(
                        t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                    );
                    setBasketCount(null);
                } else {
                    setBasketCount(preferColorBasketCount(allowed, m));
                }
            } catch (e) {
                if (!cancelled) {
                    const reason = reportColorBasketsUnavailable(
                        { message: e?.message || "Failed to load color baskets" },
                        "JigsawColorBasketOverlay/meta-error",
                    );
                    setError(
                        t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                    );
                    setMeta(null);
                    setBasketCount(null);
                }
            } finally {
                if (!cancelled) setLoadingMeta(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, jigsawId, t]);

    useEffect(() => {
        if (!open || !jigsawId || basketCount == null) return undefined;
        if (meta?.available === false) return undefined;

        let cancelled = false;
        (async () => {
            setLoadingBaskets(true);
            setError("");
            setSessionByIndex({});
            try {
                const r = await JigsawColorBasketPlayService.getColorBaskets(jigsawId, basketCount);
                if (cancelled) return;
                setResult(r);
                if (r?.available === false) {
                    const reason = reportColorBasketsUnavailable(r, "JigsawColorBasketOverlay/baskets");
                    setError(
                        t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                    );
                } else {
                    const actual = Array.isArray(r?.baskets) ? r.baskets.length : 0;
                    const reported = Number(r?.basketCount ?? basketCount);
                    const allowedRaw = Array.isArray(r?.allowedCounts)
                        ? r.allowedCounts
                        : Array.isArray(meta?.allowedCounts)
                          ? meta.allowedCounts
                          : [];
                    const allowed = allowedRaw
                        .map(Number)
                        .filter((n) => Number.isFinite(n) && n >= 2);
                    // Clustering used to return more baskets than K; prefer the actual count when allowed.
                    if (
                        actual > 0 &&
                        Number.isFinite(reported) &&
                        actual !== reported &&
                        allowed.includes(actual) &&
                        actual !== Number(basketCount)
                    ) {
                        setBasketCount(actual);
                        return;
                    }
                    const sessions = await JigsawColorBasketPlayService.listSessions(
                        jigsawId,
                        basketCount,
                    );
                    if (cancelled) return;
                    const map = {};
                    if (sessions.success && Array.isArray(sessions.data)) {
                        for (const s of sessions.data) {
                            const idx = Number(s?.basketIndex);
                            if (Number.isInteger(idx) && idx >= 0) {
                                map[idx] = s;
                            }
                        }
                    }
                    setSessionByIndex(map);
                }
            } catch (e) {
                if (!cancelled) {
                    const reason = reportColorBasketsUnavailable(
                        { message: e?.message || "Failed to load baskets" },
                        "JigsawColorBasketOverlay/baskets-error",
                    );
                    setResult(null);
                    setError(
                        t("puzzle.jigsaw.baskets-unavailable", {
                            reason,
                            defaultValue: "Color baskets unavailable: {{reason}}",
                        }),
                    );
                }
            } finally {
                if (!cancelled) setLoadingBaskets(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, jigsawId, basketCount, meta?.available, t]);

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const isBasketDeployed = useCallback(
        (idx) => {
            const session = sessionByIndex[Number(idx)];
            return String(session?.playState || "").trim().toUpperCase() === "DEPLOYED";
        },
        [sessionByIndex],
    );

    const navigateToBasket = useCallback(
        (idx) => {
            if (!storyId || !jigsawId || idx == null || basketCount == null) return;
            const basket = baskets.find((b) => Number(b?.index) === Number(idx)) ?? null;
            onClose?.();
            if (typeof onBasketChosen === "function") {
                onBasketChosen(idx, {
                    basketCount,
                    baskets,
                    basket,
                });
                return;
            }
            navigate(
                `/puzzle/play-jigsaw/${encodeURIComponent(storyId)}/board/${encodeURIComponent(jigsawId)}/basket/${encodeURIComponent(idx)}?basketCount=${encodeURIComponent(basketCount)}`,
            );
        },
        [storyId, jigsawId, basketCount, baskets, navigate, onClose, onBasketChosen],
    );

    const openBasketPage = useCallback(
        async (idx) => {
            if (!storyId || !jigsawId || idx == null || basketCount == null) return;
            if (isBasketDeployed(idx) || unlockingIndex != null) return;

            const basket = baskets.find((b) => Number(b?.index) === Number(idx));
            const session = sessionByIndex[Number(idx)];
            const needsPay = basketNeedsUnlock(basket) && !session;

            if (!needsPay) {
                navigateToBasket(idx);
                return;
            }

            setError("");
            try {
                const info = await UserService.getUserInfo();
                if (!info?.cloudUser) {
                    setLoginDialogOpen(true);
                    return;
                }
            } catch {
                setLoginDialogOpen(true);
                return;
            }

            setUnlockingIndex(Number(idx));
            try {
                await JigsawColorBasketPlayService.unlockColorBaskets(jigsawId, basketCount, idx);
                setResult((prev) => {
                    if (!prev || !Array.isArray(prev.baskets)) return prev;
                    return {
                        ...prev,
                        baskets: prev.baskets.map((b) =>
                            Number(b?.index) === Number(idx)
                                ? { ...b, unlocked: true, requiresUnlock: false }
                                : b,
                        ),
                    };
                });
                navigateToBasket(idx);
            } catch (e) {
                const msg = e?.message || "";
                if (/insufficient/i.test(msg)) {
                    setError("");
                    setInsufficientCoinsOpen(true);
                } else if (/auth|unauthor|login required|authentication/i.test(msg)) {
                    setError("");
                    setLoginDialogOpen(true);
                } else {
                    setError(
                        msg ||
                            t(
                                "puzzle.jigsaw.basket-unlock-failed",
                                "Could not unlock this basket. Please try again.",
                            ),
                    );
                }
            } finally {
                setUnlockingIndex(null);
            }
        },
        [
            storyId,
            jigsawId,
            basketCount,
            baskets,
            sessionByIndex,
            isBasketDeployed,
            unlockingIndex,
            navigateToBasket,
            t,
        ],
    );

    useEffect(() => {
        if (autoPickBasketIndex == null || !open || loadingBaskets) return undefined;
        const idx = Number(autoPickBasketIndex);
        if (!baskets.some((b) => Number(b?.index) === idx)) return undefined;
        const timer = window.setTimeout(() => navigateToBasket(idx), autoPickDelayMs);
        return () => window.clearTimeout(timer);
    }, [
        autoPickBasketIndex,
        autoPickDelayMs,
        baskets,
        loadingBaskets,
        navigateToBasket,
        open,
    ]);

    const [checkoutBusy, setCheckoutBusy] = useState(false);

    const goToAssistantCoinsCheckout = useCallback(async () => {
        setInsufficientCoinsOpen(false);
        setCheckoutBusy(true);
        try {
            const result = await SubscriptionService.handleSubscribe(
                "pack300",
                SUBSCRIPTION_PLANS,
            );
            if (result?.requiresLogin) {
                setLoginDialogOpen(true);
                return;
            }
            if (!result?.success) {
                setError(
                    result?.error ||
                        t(
                            "puzzle.jigsaw.basket-unlock-checkout-failed",
                            "Could not start checkout. Please try again.",
                        ),
                );
            }
        } finally {
            setCheckoutBusy(false);
        }
    }, [t]);

    const goToSubscriptionPlans = useCallback(() => {
        setInsufficientCoinsOpen(false);
        onClose?.();
        navigate("/subscription/plans");
    }, [navigate, onClose]);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                sx={dialogSx}
                PaperProps={{
                    sx: {
                        bgcolor: "background.paper",
                        backgroundImage: "none",
                        borderRadius: 2,
                        maxHeight: "90vh",
                        m: 2,
                        ...paperSx,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        pt: 1.5,
                        pb: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            color: "#3BA666",
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {puzzleTitle || t("puzzle.jigsaw.color-baskets", "Color baskets")}
                    </Typography>
                    <Tooltip title={t("common.close", "Close")} arrow>
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            sx={{ flexShrink: 0, color: "#3BA666" }}
                            aria-label={t("common.close", "Close")}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <DialogContent sx={{ p: 2, pt: 2, overflowY: "auto" }}>
                    <Stack spacing={2}>
                        {loadingMeta || loadingBaskets ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress size={36} sx={{ color: "#3BA666" }} />
                            </Box>
                        ) : null}

                        {error ? (
                            <Stack spacing={1}>
                                <Typography color="error" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                    {error}
                                </Typography>
                                {isAdminRole && storyId && jigsawId ? (
                                    <Link
                                        component={RouterLink}
                                        to={`/admin/jigsaw/browse/${encodeURIComponent(storyId)}/color-baskets/${encodeURIComponent(jigsawId)}`}
                                        variant="body2"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        Open admin Color Baskets detail
                                    </Link>
                                ) : null}
                            </Stack>
                        ) : null}

                        {showDiagnostics ? (
                            <Box
                                sx={{
                                    p: 1.25,
                                    borderRadius: 1,
                                    bgcolor: "rgba(0,0,0,0.04)",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        fontWeight: 700,
                                        color: "text.secondary",
                                        mb: 0.5,
                                    }}
                                >
                                    Diagnostics
                                </Typography>
                                {debugLines.map((line, i) => (
                                    <Typography
                                        key={`diag-${i}`}
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                        sx={{
                                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                            fontSize: 11,
                                            lineHeight: 1.45,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {line}
                                    </Typography>
                                ))}
                                {!loadingBaskets && allowedBasketCounts.length > 0 ? (
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        useFlexGap
                                        flexWrap="wrap"
                                        alignItems="center"
                                        sx={{ mt: 1.25 }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {t("puzzle.jigsaw.basket-count-label", "Baskets:")}
                                        </Typography>
                                        {allowedBasketCounts.map((k) => (
                                            <Chip
                                                key={k}
                                                size="small"
                                                label={k}
                                                clickable
                                                color={Number(basketCount) === k ? "primary" : "default"}
                                                variant={Number(basketCount) === k ? "filled" : "outlined"}
                                                onClick={() => setBasketCount(k)}
                                                sx={{ fontWeight: 700, minWidth: 36 }}
                                            />
                                        ))}
                                    </Stack>
                                ) : null}
                                {!loadingBaskets && baskets.length > 0 ? (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 1, lineHeight: 1.45 }}
                                    >
                                        {t("puzzle.jigsaw.basket-unlock-hint", {
                                            free: freeMaxCount,
                                            count: unlockCoinCost,
                                            defaultValue:
                                                "The first {{free}} baskets are free; more cost {{count}} coins each.",
                                        })}
                                    </Typography>
                                ) : null}
                            </Box>
                        ) : null}

                        {!loadingMeta && meta?.available !== false ? (
                            <>
                                {!loadingBaskets && baskets.length > 0 ? (
                                    <Stack spacing={2} alignItems="center">
                                        {!showDiagnostics ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ textAlign: "center", px: 1 }}
                                            >
                                                {t("puzzle.jigsaw.basket-unlock-hint", {
                                                    free: freeMaxCount,
                                                    count: unlockCoinCost,
                                                    defaultValue:
                                                        "The first {{free}} baskets are free; more cost {{count}} coins each.",
                                                })}
                                            </Typography>
                                        ) : null}

                                        <Box sx={{ width: "100%" }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 700, mb: 1.5, textAlign: "center" }}
                                            >
                                                {t("puzzle.jigsaw.color-baskets", "Color Baskets")}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    width: "100%",
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    aria-label={t(
                                                        "puzzle.jigsaw.baskets-scroll-left",
                                                        "Scroll baskets left",
                                                    )}
                                                    onClick={() => scrollBasketsBy(-1)}
                                                    disabled={!basketScrollHints.left}
                                                    sx={{
                                                        flexShrink: 0,
                                                        color: "#1B4332",
                                                        bgcolor: "rgba(255,255,255,0.85)",
                                                        border: "1px solid rgba(27, 67, 50, 0.16)",
                                                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                    }}
                                                >
                                                    <ChevronLeftIcon />
                                                </IconButton>
                                            <Box
                                                ref={basketScrollRef}
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    flexWrap: "nowrap",
                                                    gap: { xs: 1.25, sm: 1.5 },
                                                    overflowX: "auto",
                                                    overflowY: "hidden",
                                                    WebkitOverflowScrolling: "touch",
                                                    scrollSnapType: "x mandatory",
                                                    flex: 1,
                                                    minWidth: 0,
                                                    pb: 0.75,
                                                    px: 0.25,
                                                    scrollbarWidth: "thin",
                                                    "&::-webkit-scrollbar": { height: 6 },
                                                    "&::-webkit-scrollbar-thumb": {
                                                        bgcolor: "rgba(0,0,0,0.25)",
                                                        borderRadius: 999,
                                                    },
                                                }}
                                            >
                                                {baskets.map((basket) => {
                                                    const hex = basket?.dominantColorHex || "#999999";
                                                    const pieceIds = Array.isArray(basket?.pieceIds)
                                                        ? basket.pieceIds
                                                        : [];
                                                    const pieceCount = pieceIds.length;
                                                    const idx = basket?.index;
                                                    const displayN = Number(idx) + 1;
                                                    const session = sessionByIndex[Number(idx)];
                                                    const playState = String(session?.playState || "")
                                                        .trim()
                                                        .toUpperCase();
                                                    const inProgress =
                                                        Boolean(session) && playState !== "DEPLOYED";
                                                    const deployed = playState === "DEPLOYED";
                                                    const locked =
                                                        basketNeedsUnlock(basket) && !session;
                                                    const unlocking = unlockingIndex === Number(idx);
                                                    const isFreeTier =
                                                        Number.isInteger(Number(idx)) &&
                                                        Number(idx) < Number(freeMaxCount);
                                                    return (
                                                        <Box
                                                            key={idx ?? hex}
                                                            component="button"
                                                            type="button"
                                                            disabled={deployed || unlockingIndex != null}
                                                            onClick={() => openBasketPage(idx)}
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                p: 0,
                                                                m: 0,
                                                                border: "none",
                                                                bgcolor: "transparent",
                                                                cursor:
                                                                    deployed || unlockingIndex != null
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                flex: "0 0 auto",
                                                                width: { xs: 88, sm: 100 },
                                                                minHeight: 48,
                                                                scrollSnapAlign: "start",
                                                                opacity: deployed ? 0.55 : 1,
                                                                "&:hover .basket-tile":
                                                                    deployed || unlockingIndex != null
                                                                        ? undefined
                                                                        : {
                                                                              outline: "2px solid #3BA666",
                                                                              outlineOffset: 1,
                                                                          },
                                                            }}
                                                        >
                                                            <Box
                                                                className="basket-tile"
                                                                aria-hidden
                                                                sx={{
                                                                    position: "relative",
                                                                    width: "100%",
                                                                    aspectRatio: "1 / 1",
                                                                    borderRadius: 1.75,
                                                                    bgcolor: hex,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    boxShadow:
                                                                        "0 2px 6px rgba(0,0,0,0.2)",
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        position: "absolute",
                                                                        top: 5,
                                                                        left: 5,
                                                                        ...colorBasketNumberBadgeSx({
                                                                            size: 20,
                                                                        }),
                                                                    }}
                                                                >
                                                                    {displayN}
                                                                </Box>
                                                                {isFreeTier && !locked && !deployed ? (
                                                                    <Box
                                                                        sx={{
                                                                            position: "absolute",
                                                                            top: 4,
                                                                            right: 4,
                                                                            px: 0.6,
                                                                            py: 0.15,
                                                                            borderRadius: 0.75,
                                                                            bgcolor: "#FFF8E7",
                                                                            color: "#9A3412",
                                                                            border: "1px solid rgba(234, 179, 8, 0.65)",
                                                                            boxShadow:
                                                                                "0 1px 3px rgba(0,0,0,0.22)",
                                                                            fontSize: 9,
                                                                            fontWeight: 800,
                                                                            lineHeight: 1.2,
                                                                            letterSpacing: "0.02em",
                                                                            textTransform: "uppercase",
                                                                        }}
                                                                    >
                                                                        {t(
                                                                            "puzzle.jigsaw.basket-free",
                                                                            "Free",
                                                                        )}
                                                                    </Box>
                                                                ) : null}
                                                                {!isFreeTier && !deployed ? (
                                                                    <Box
                                                                        sx={{
                                                                            position: "absolute",
                                                                            top: 4,
                                                                            right: 4,
                                                                            px: 0.6,
                                                                            py: 0.15,
                                                                            borderRadius: 0.75,
                                                                            bgcolor: "#1E3A5F",
                                                                            color: "#FDE68A",
                                                                            border: "1px solid rgba(253, 230, 138, 0.55)",
                                                                            boxShadow:
                                                                                "0 1px 3px rgba(0,0,0,0.28)",
                                                                            fontSize: 9,
                                                                            fontWeight: 800,
                                                                            lineHeight: 1.2,
                                                                            letterSpacing: "0.02em",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {t(
                                                                            "puzzle.jigsaw.basket-unlock-cost",
                                                                            {
                                                                                count: unlockCoinCost,
                                                                                defaultValue:
                                                                                    "{{count}} coins",
                                                                            },
                                                                        )}
                                                                    </Box>
                                                                ) : null}
                                                                {unlocking ? (
                                                                    <CircularProgress
                                                                        size={22}
                                                                        sx={{ color: "#fff" }}
                                                                    />
                                                                ) : (
                                                                    <FaShoppingBasket
                                                                        size={34}
                                                                        color="#fff"
                                                                        aria-hidden
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: "text.secondary",
                                                                    fontSize: 11,
                                                                    lineHeight: 1.2,
                                                                    textAlign: "center",
                                                                }}
                                                            >
                                                                {t("puzzle.jigsaw.basket-piece-count", {
                                                                    count: pieceCount,
                                                                    defaultValue: "{{count}} pieces",
                                                                })}
                                                            </Typography>
                                                            {inProgress ? (
                                                                <Chip
                                                                    size="small"
                                                                    label={t(
                                                                        "puzzle.jigsaw.basket-in-progress",
                                                                        "In progress",
                                                                    )}
                                                                    sx={{
                                                                        height: 18,
                                                                        fontWeight: 700,
                                                                        fontSize: 9,
                                                                        bgcolor: "rgba(59,166,102,0.14)",
                                                                        color: "#2e8a52",
                                                                        border: "1px solid",
                                                                        borderColor:
                                                                            "rgba(59,166,102,0.45)",
                                                                        "& .MuiChip-label": {
                                                                            px: 0.75,
                                                                        },
                                                                    }}
                                                                />
                                                            ) : null}
                                                            {deployed ? (
                                                                <Chip
                                                                    size="small"
                                                                    label={t(
                                                                        "puzzle.jigsaw.basket-deployed",
                                                                        "Deployed",
                                                                    )}
                                                                    sx={{
                                                                        height: 18,
                                                                        fontWeight: 700,
                                                                        fontSize: 9,
                                                                        bgcolor: "rgba(99,102,241,0.12)",
                                                                        color: "#4338ca",
                                                                        border: "1px solid",
                                                                        borderColor:
                                                                            "rgba(99,102,241,0.4)",
                                                                        "& .MuiChip-label": {
                                                                            px: 0.75,
                                                                        },
                                                                    }}
                                                                />
                                                            ) : null}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                                <IconButton
                                                    size="small"
                                                    aria-label={t(
                                                        "puzzle.jigsaw.baskets-scroll-right",
                                                        "Scroll baskets right",
                                                    )}
                                                    onClick={() => scrollBasketsBy(1)}
                                                    disabled={!basketScrollHints.right}
                                                    sx={{
                                                        flexShrink: 0,
                                                        color: "#1B4332",
                                                        bgcolor: "rgba(255,255,255,0.85)",
                                                        border: "1px solid rgba(27, 67, 50, 0.16)",
                                                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                    }}
                                                >
                                                    <ChevronRightIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {imageUrl && grid > 0 ? (
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    maxWidth: 320,
                                                    mx: "auto",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <AdminColorBasketMiniMap
                                                    baskets={baskets}
                                                    imageUrl={imageUrl}
                                                    grid={grid}
                                                    size={null}
                                                    compact
                                                    oneBasedLabels
                                                    onBasketClick={openBasketPage}
                                                />
                                            </Box>
                                        ) : null}
                                    </Stack>
                                ) : null}

                                {!loadingBaskets && baskets.length === 0 && result ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t("puzzle.jigsaw.no-baskets", "No baskets returned.")}
                                    </Typography>
                                ) : null}
                            </>
                        ) : null}
                    </Stack>
                </DialogContent>
            </Dialog>

            <LoginConfirmDialog
                open={loginDialogOpen}
                onClose={() => setLoginDialogOpen(false)}
                onConfirm={() => {
                    navigate("/login", {
                        state: { from: { pathname: window.location.pathname } },
                    });
                }}
                title={t("puzzle.jigsaw.basket-unlock-login-title", "Login required")}
                message={t(
                    "puzzle.jigsaw.basket-unlock-need-login",
                    "Please sign in to unlock color baskets with coins.",
                )}
            />

            <Dialog
                open={insufficientCoinsOpen}
                onClose={() => setInsufficientCoinsOpen(false)}
                maxWidth="xs"
                fullWidth
                aria-labelledby="basket-insufficient-coins-title"
            >
                <DialogTitle id="basket-insufficient-coins-title">
                    {t("puzzle.jigsaw.basket-unlock-insufficient-title", "Not enough coins")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                        {t("puzzle.jigsaw.basket-unlock-insufficient", {
                            count: unlockCoinCost,
                            defaultValue:
                                "Not enough coins. Each basket costs {{count}} coins.",
                        })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        {t(
                            "puzzle.jigsaw.basket-unlock-insufficient-hint",
                            "Buy 300 Coins ($0.99), then come back to unlock this basket.",
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, flexWrap: "wrap", gap: 1 }}>
                    <Button onClick={() => setInsufficientCoinsOpen(false)}>
                        {t("btn.cancel", "Cancel")}
                    </Button>
                    <Button onClick={goToSubscriptionPlans} disabled={checkoutBusy}>
                        {t("puzzle.jigsaw.basket-unlock-see-plans", "See all plans")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={goToAssistantCoinsCheckout}
                        disabled={checkoutBusy}
                        sx={{ bgcolor: "#3BA666", "&:hover": { bgcolor: "#2e8a52" } }}
                    >
                        {checkoutBusy
                            ? t("common.loading", "Loading…")
                            : t(
                                  "puzzle.jigsaw.basket-unlock-buy-assistant-coins",
                                  "Buy 300 Coins — $0.99",
                              )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
